import { useQuery } from "@apollo/client";
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { DISCREPANCY_KIND_NAMES } from "encamp-shared/src/waste/discrepancyKindNames";
import { formatEpaCorrectionPreviewLine } from "encamp-shared/src/waste/epaCorrectionRequests";
import {
  type BulkManifestDiscrepanciesQuery,
  DiscrepancyKind,
  DiscrepancyStatus,
  WasteManifestSource,
} from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useMemo, useState } from "react";

import { type ComplianceItemEpa, mapEpaCorrectionDiscrepancy } from "./CompliancePane.helpers";
import { BULK_MANIFEST_DISCREPANCIES_QUERY } from "./DiscrepancyReviewDialog";
import {
  formatDisplayValue,
  getDiscrepancyStatus,
  getFieldLabel,
  getStatusColor,
  getStatusLabel,
} from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancySection } from "./DiscrepancySection";
import { DiscrepancySummaryBar } from "./DiscrepancySummaryBar";
import { SOURCE_LABEL } from "./manifestSourceDisplay";
import { buildDiscrepancyBreakdown } from "./sections/discrepancyBreakdown";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];
type CustomerSectionKey = "epaCorrection" | "field";

interface CustomerDiscrepancyDialogProps {
  processedManifestId: string;
  manifestTrackingNumber?: string | null;
  onClose: () => void;
}

const EPA_CORRECTION = DISCREPANCY_KIND_NAMES.EPA_CORRECTION_REQUEST;

const SECTION_LABELS: Record<CustomerSectionKey, string> = {
  epaCorrection: DISCREPANCY_KIND_NAMES.EPA_CORRECTION_REQUEST.heading,
  field: DISCREPANCY_KIND_NAMES.FIELD_DISCREPANCY.heading,
};

/**
 * Mirrors the customer branch (`!isStaff`) of the row selection in
 * `ManifestDetailSection.tsx` — every non-ignored `FieldDiscrepancy` at any
 * status, plus `EpaCorrectionRequest` rows when the flag is on — so the tile's
 * count and this dialog's rows never drift apart again.
 */
function customerDiscrepancies(
  discrepancies: readonly Discrepancy[],
  showEpaCorrections: boolean
): Discrepancy[] {
  return discrepancies.filter((discrepancy) => {
    if (discrepancy.status === DiscrepancyStatus.Ignored) return false;
    if (discrepancy.kind === DiscrepancyKind.FieldDiscrepancy) return true;
    return showEpaCorrections && discrepancy.kind === DiscrepancyKind.EpaCorrectionRequest;
  });
}

function correctionSummary(
  epaItems: readonly ComplianceItemEpa[],
  fieldDiscrepancies: readonly Discrepancy[]
): string {
  // EPA section/comment text is the card title — keep the subheader as a
  // pointer, not a second copy of the same § / quote string.
  const fieldLabels = [...new Set(fieldDiscrepancies.map((item) => getFieldLabel(item.field)))];
  if (epaItems.length > 0 && fieldLabels.length === 0) {
    return epaItems.length === 1
      ? `Review the ${EPA_CORRECTION.one} below.`
      : `Review ${epaItems.length} ${EPA_CORRECTION.many} below.`;
  }
  if (fieldLabels.length === 1 && epaItems.length === 0) {
    return `${DISCREPANCY_KIND_NAMES.FIELD_DISCREPANCY.one}: ${fieldLabels[0]}`;
  }
  if (fieldLabels.length > 1 && epaItems.length === 0) {
    return `${DISCREPANCY_KIND_NAMES.FIELD_DISCREPANCY.many}: ${fieldLabels.join(", ")}`;
  }
  if (epaItems.length > 0 && fieldLabels.length > 0) {
    return `Review the ${EPA_CORRECTION.many} and ${DISCREPANCY_KIND_NAMES.FIELD_DISCREPANCY.many.toLowerCase()} below.`;
  }
  return "Review the correction details below.";
}

/**
 * Grouped by section rather than a flat list: `EpaCorrectionCard` and
 * `FieldDiscrepancyCard` build their chip label from different functions, so
 * the same stored status prints a different word depending on kind ("Pending"
 * on an EPA row means work is underway; "Pending Review" on a field row means
 * nobody has looked yet). The subheads reuse `SECTION_LABELS` so they track
 * the headings rendered above the cards. "Resolved" is the one label both
 * kinds share, so it stays a single trailing entry under neither subhead.
 */
function CustomerStatusGlossary() {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" component="h2" sx={{ mb: 1 }}>
        What these statuses mean
      </Typography>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {SECTION_LABELS.epaCorrection}
        </Typography>
        <Typography variant="body2">
          <strong>Critical:</strong> EPA requested a correction that needs prompt attention.
        </Typography>
        <Typography variant="body2">
          <strong>Pending:</strong> The correction must be made in e-Manifest (RCRAInfo). It
          resolves once e-Manifest shows the corrected value.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {SECTION_LABELS.field}
        </Typography>
        <Typography variant="body2">
          <strong>Pending Review:</strong> Encamp has not reviewed this conflict between manifest
          data sources yet.
        </Typography>
        <Typography variant="body2">
          <strong>Needs correction in e-Manifest:</strong> The value must be corrected in e-Manifest
          (RCRAInfo). It resolves once e-Manifest shows the corrected value.
        </Typography>
        <Typography variant="body2">
          <strong>Resolved:</strong> The corrected data is now reflected in the manifest.
        </Typography>
      </Stack>
    </Paper>
  );
}

function SourceValue({ label, value }: { label: string; value?: string | null }) {
  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 112 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {formatDisplayValue(value)}
      </Typography>
    </Stack>
  );
}

function FieldDiscrepancyCard({ discrepancy }: { discrepancy: Discrepancy }) {
  const status = getDiscrepancyStatus(discrepancy);
  const statusLabel = getStatusLabel(status);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="subtitle2" component="h3">
            {getFieldLabel(discrepancy.field)}
          </Typography>
          {discrepancy.wasteLineUnifiedId && (
            <Typography variant="caption" color="text.secondary">
              Waste line {discrepancy.wasteLineNumber ?? "item"}
            </Typography>
          )}
        </Box>
        <Chip size="small" label={statusLabel} color={getStatusColor(status)} variant="outlined" />
      </Stack>
      <Stack spacing={0.75} sx={{ mt: 1.5 }}>
        <SourceValue label="e-Manifest" value={discrepancy.eManifestValue} />
        <SourceValue label="Vendor" value={discrepancy.vendorValue} />
        <SourceValue
          label={SOURCE_LABEL[WasteManifestSource.ManifestDocument]}
          value={discrepancy.manifestDocumentValue}
        />
      </Stack>
    </Paper>
  );
}

function EpaCorrectionCard({ item }: { item: ComplianceItemEpa }) {
  let statusLabel = "Critical";
  if (item.status === "pending") statusLabel = "Pending";
  if (item.status === "resolved") statusLabel = "Resolved";
  // Title and list derive from one index so a blank leading section cannot push
  // the titled section into the list a second time. Blank sections are dropped
  // from the list too, since `§  – ""` tells the reader nothing.
  const titleIndex = item.sections.findIndex(
    (section) => formatEpaCorrectionPreviewLine(section) !== null
  );
  const titleSection = titleIndex >= 0 ? item.sections[titleIndex] : undefined;
  const title =
    (titleSection && formatEpaCorrectionPreviewLine(titleSection)) ?? EPA_CORRECTION.one;
  const extraSections = item.sections.filter(
    (section, index) => index !== titleIndex && formatEpaCorrectionPreviewLine(section) !== null
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Typography variant="subtitle2" component="h3" sx={{ wordBreak: "break-word" }}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={statusLabel}
          color={getStatusColor(item.status)}
          variant="outlined"
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Requested {item.requestedAt} by {item.initiator} · response due {item.deadline}
      </Typography>
      {extraSections.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          {extraSections.map((section, index) => (
            <Typography
              key={`${section.sectionNumber}-${index}`}
              variant="body2"
              sx={{ wordBreak: "break-word" }}
            >
              <strong>§ {section.sectionNumber}</strong> – &quot;{section.comments}&quot;
            </Typography>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export function CustomerDiscrepancyDialog({
  processedManifestId,
  manifestTrackingNumber,
  onClose,
}: CustomerDiscrepancyDialogProps) {
  const { featureFlags } = useFeatureFlags();
  const showEpaCorrections = !!featureFlags[FeatureFlagKey.WasteEpaCorrectionDiscrepancies];
  const { data, loading } = useQuery(BULK_MANIFEST_DISCREPANCIES_QUERY, {
    variables: { processedManifestIds: [processedManifestId] },
  });
  const [activeSection, setActiveSection] = useState<CustomerSectionKey | null>(null);

  const visibleDiscrepancies = useMemo(
    () => customerDiscrepancies(data?.bulkManifestDiscrepancies ?? [], showEpaCorrections),
    [data?.bulkManifestDiscrepancies, showEpaCorrections]
  );
  const fieldDiscrepancies = useMemo(
    () =>
      visibleDiscrepancies.filter(
        (discrepancy) => discrepancy.kind === DiscrepancyKind.FieldDiscrepancy
      ),
    [visibleDiscrepancies]
  );
  const epaItems = useMemo(() => {
    const items: ComplianceItemEpa[] = [];
    for (const discrepancy of visibleDiscrepancies) {
      if (discrepancy.kind !== DiscrepancyKind.EpaCorrectionRequest) continue;
      const item = mapEpaCorrectionDiscrepancy({
        ...discrepancy,
        status: getDiscrepancyStatus(discrepancy),
      });
      if (item) items.push(item);
    }
    return items;
  }, [visibleDiscrepancies]);

  const breakdown = useMemo(
    () => buildDiscrepancyBreakdown(visibleDiscrepancies),
    [visibleDiscrepancies]
  );
  const countFor = (key: CustomerSectionKey) =>
    breakdown.rows.find((row) => row.key === key)?.counts.total ?? 0;
  const availableSections: CustomerSectionKey[] = [];
  if (epaItems.length > 0) availableSections.push("epaCorrection");
  if (fieldDiscrepancies.length > 0) availableSections.push("field");
  const summaryTiles = availableSections.map((key) => ({
    key,
    label: SECTION_LABELS[key],
    count: countFor(key),
  }));
  const showSection = (key: CustomerSectionKey) => activeSection == null || activeSection === key;
  const titleSuffix = manifestTrackingNumber ?? processedManifestId.slice(0, 8);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title={`Manifest discrepancies — #${titleSuffix}`}
      subheader={correctionSummary(epaItems, fieldDiscrepancies)}
      hideCancelButton
      primaryActionButton={
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      }
    >
      <CustomerStatusGlossary />
      {loading && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      )}
      {!loading && availableSections.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No discrepancies on this manifest
        </Typography>
      )}
      {!loading && availableSections.length > 0 && (
        <>
          <DiscrepancySummaryBar
            tiles={summaryTiles}
            loading={false}
            activeKeys={activeSection ? [activeSection] : []}
            onTileClick={(key) => setActiveSection((current) => (current === key ? null : key))}
          />
          {showSection("epaCorrection") && epaItems.length > 0 && (
            <DiscrepancySection
              title={SECTION_LABELS.epaCorrection}
              count={epaItems.length}
              fillContainer
            >
              <Stack spacing={1.5} sx={{ p: 1.5 }}>
                {epaItems.map((item) => (
                  <EpaCorrectionCard key={item.id} item={item} />
                ))}
              </Stack>
            </DiscrepancySection>
          )}
          {showSection("field") && fieldDiscrepancies.length > 0 && (
            <DiscrepancySection
              title={SECTION_LABELS.field}
              count={fieldDiscrepancies.length}
              fillContainer
            >
              <Stack spacing={1.5} sx={{ p: 1.5 }}>
                {fieldDiscrepancies.map((discrepancy) => (
                  <FieldDiscrepancyCard key={discrepancy.id} discrepancy={discrepancy} />
                ))}
              </Stack>
            </DiscrepancySection>
          )}
        </>
      )}
    </Dialog>
  );
}
